/**
 * Определение рода субъекта по строке или типу.
 * Поддерживает физлица, ИП и юрлица (нейтральный род).
 */

// Словарь распространённых мужских имён
const MALE_NAMES = new Set([
  'александр', 'алексей', 'андрей', 'антон', 'артем', 'артём', 'борис', 'вадим', 'валентин',
  'валерий', 'василий', 'виктор', 'виталий', 'владимир', 'владислав', 'вячеслав',
  'геннадий', 'георгий', 'григорий', 'даниил', 'денис', 'дмитрий', 'евгений', 'егор',
  'иван', 'игорь', 'илья', 'кирилл', 'константин', 'леонид', 'максим', 'михаил',
  'николай', 'олег', 'павел', 'петр', 'пётр', 'роман', 'сергей', 'станислав',
  'степан', 'тимофей', 'федор', 'фёдор', 'юрий', 'ярослав'
]);

// Словарь распространённых женских имён
const FEMALE_NAMES = new Set([
  'александра', 'алина', 'алла', 'анастасия', 'анна', 'валентина', 'валерия', 'вера',
  'виктория', 'галина', 'дарья', 'екатерина', 'елена', 'елизавета', 'жанна', 'зоя',
  'инна', 'ирина', 'кристина', 'ксения', 'лариса', 'лидия', 'любовь', 'людмила',
  'маргарита', 'марина', 'мария', 'надежда', 'наталья', 'нина', 'оксана', 'ольга',
  'полина', 'раиса', 'светлана', 'софья', 'тамара', 'татьяна', 'юлия'
]);

const LEGAL_MARKERS = [
  'ооо', 'оао', 'пао', 'зао', 'нко', 'ао', 'оао', 'оооо',
  'общество', 'компания', 'фонд', 'партнерство', 'партнёрство',
  'ассоциация', 'кооператив', 'товарищество', 'учреждение', 'университет'
];

const IP_PREFIX_REGEX = /^\s*(ип|индивидуальный предприниматель)\s+/i;

function isLikelyLegalName(value = '') {
  const normalized = value.toLowerCase();
  return LEGAL_MARKERS.some(marker => normalized.includes(marker));
}

function stripIpPrefix(value = '') {
  return value.replace(IP_PREFIX_REGEX, '').trim();
}

function hasTurkicMaleMarker(parts = []) {
  return parts.some((part) => /^(оглы|оглу|улы|уулу)$/i.test(part));
}

function hasTurkicFemaleMarker(parts = []) {
  return parts.some((part) => /^(кызы|гызы)$/i.test(part));
}

/**
 * Определяет род по ФИО (без учёта ИП/типов)
 */
export function detectGender(fullName) {
  if (!fullName || typeof fullName !== 'string') {
    return 'unknown';
  }

  const normalized = fullName.trim().toLowerCase();
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return 'unknown';
  }

  // 0. Национальные маркеры пола в конце ФИО: "... оглы"/"... кызы"
  const tail = parts.slice(2);
  if (hasTurkicMaleMarker(tail)) return 'male';
  if (hasTurkicFemaleMarker(tail)) return 'female';

  // 1. Отчество
  if (parts.length >= 3) {
    const patronymic = parts[2];
    if (patronymic.endsWith('вич') || patronymic.endsWith('ич')) return 'male';
    if (patronymic.endsWith('вна') || patronymic.endsWith('ична') || patronymic.endsWith('инична')) return 'female';
  }

  // 2. Имя
  if (parts.length >= 2) {
    const firstName = parts[1];
    if (MALE_NAMES.has(firstName)) return 'male';
    if (FEMALE_NAMES.has(firstName)) return 'female';
    if (firstName.endsWith('а') || firstName.endsWith('я')) return 'female';
    if (firstName.endsWith('й') || firstName.endsWith('ий') || /[бвгджзклмнпрстфхцчшщ]$/.test(firstName)) return 'male';
  }

  // 3. Фамилия
  const lastName = parts[0];
  if (lastName.endsWith('ова') || lastName.endsWith('ева') ||
    lastName.endsWith('ина') || lastName.endsWith('ая') ||
    lastName.endsWith('ская') || lastName.endsWith('цкая')) {
    return 'female';
  }
  if (lastName.endsWith('ов') || lastName.endsWith('ев') ||
    lastName.endsWith('ин') || lastName.endsWith('ый') ||
    lastName.endsWith('ий') || lastName.endsWith('ский') ||
    lastName.endsWith('цкий')) {
    return 'male';
  }

  return 'unknown';
}

/**
 * Проверяет, похоже ли значение на ФИО физлица
 */
export function isPersonName(value) {
  if (!value || typeof value !== 'string') return false;
  const normalized = value.trim();
  const parts = normalized.split(/\s+/).filter(Boolean);
  if (parts.length < 2 || parts.length > 4) return false;
  return parts.every(part => /^[А-ЯЁ]/.test(part));
}

/**
 * Определяет род/тип субъекта с учётом явного типа, ИП и юрлиц
 * @param {string} value - исходное значение переменной
 * @param {object} options - дополнительные подсказки
 * @param {string} options.explicitType - тип субъекта ('legal', 'individual', 'entrepreneur', ...)
 * @returns {'male'|'female'|'neuter'|'unknown'}
 */
export function detectSubjectGender(value, { explicitType } = {}) {
  if (!value || typeof value !== 'string') return 'unknown';

  const normalizedType = (explicitType || '').toLowerCase();
  if (['legal', 'company', 'org', 'organization'].includes(normalizedType)) {
    return 'neuter';
  }
  if (normalizedType === 'entrepreneur') {
    const stripped = stripIpPrefix(value);
    return detectGender(stripped) || 'unknown';
  }
  if (normalizedType === 'individual') {
    return detectGender(value) || 'unknown';
  }

  if (IP_PREFIX_REGEX.test(value)) {
    const stripped = stripIpPrefix(value);
    return detectGender(stripped) || 'unknown';
  }

  if (isLikelyLegalName(value)) {
    return 'neuter';
  }

  if (isPersonName(value)) {
    return detectGender(value);
  }

  return 'neuter';
}

/**
 * Определяет род по значению переменной из данных документа
 * @param {string} variableName - Имя переменной (например, 'organizer_signatory_r')
 * @param {object} variablesData - Объект с данными всех переменных
 * @returns {'male' | 'female' | 'neuter' | 'unknown'} - Определённый род
 */
export function detectGenderFromVariable(variableName, variablesData) {
  if (!variableName || !variablesData) return 'unknown';

  const baseName = variableName.replace(/_(r|d|v|t|p)$/, '');
  const value = variablesData[baseName] || variablesData[variableName];
  if (!value || typeof value !== 'string') return 'unknown';

  let explicitType = null;
  // Для ФИО управляющего нельзя брать тип должника: у юрлица-должника
  // это давало neuter и ломало род в {муж|жен|сред:debtor_manager_name}.
  if (baseName.startsWith('debtor_manager_')) {
    explicitType = null;
  } else if (baseName.startsWith('organizer_')) {
    explicitType = variablesData.organizer_kind || variablesData.organizer_type_code || null;
  } else if (baseName.startsWith('debtor_')) {
    explicitType = variablesData.debtor_kind || variablesData.debtor_type_code || null;
  }

  return detectSubjectGender(value, { explicitType });
}

const api = {
  detectGender,
  detectGenderFromVariable,
  detectSubjectGender,
  isPersonName
};

export default api;
